import { Scroll, Crown, TrendingUp, CalendarDays, TriangleAlert, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { RitualSeal } from "./RitualSeal";
import { whatsappLink } from "../config/site";
import { useLang } from "../config/i18n";

const IMAGES = {
  prosperidade: "/pacto-prosperidade.png",
  baal: "/pacto-baal.jpeg",
  belzebu: "/pacto-belzebu.jpeg",
  crescimento: "/pacto-crescimento.png",
  ciclo: "/pacto-ciclo.png",
};

const ICONS = {
  prosperidade: Scroll,
  baal: Crown,
  belzebu: Crown,
  crescimento: TrendingUp,
  ciclo: CalendarDays,
};

export const Pactuacoes = () => {
  const { t } = useLang();
  const p = t.pactuacoes;

  return (
    <section id="pactuacoes" data-testid="pacts-section" className="relative py-24 sm:py-36 bg-[#0D0D10] border-y border-[#22222B] overflow-hidden">
      <RitualSeal className="absolute -right-40 -top-40 w-[560px] text-[#C5A059] opacity-[0.07] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6">
        <Reveal className="text-center max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{p.overline}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#F7F7F8] leading-snug mb-8">
            {p.title1} <span className="gold-text">{p.title2}</span>
          </h2>
          <p className="text-[15px] sm:text-base text-[#D1D1DB] leading-relaxed font-light mb-5">{p.intro1}</p>
          <p className="text-sm sm:text-[15px] text-[#6E6E7D] leading-relaxed font-light">{p.intro2}</p>
        </Reveal>

        <Reveal className="text-center mt-24">
          <p className="text-[11px] font-semibold tracking-[0.3em] text-[#C5A059] uppercase mb-5">{p.typesOverline}</p>
          <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug">{p.typesTitle}</h3>
        </Reveal>

        <div data-testid="pacts-cards" className="mt-12 flex flex-col gap-6">
          {p.cards.map(({ id, title, text, items }, i) => {
            const Icon = ICONS[id];
            return (
              <Reveal key={id} delay={0.05}>
                <div
                  data-testid={`pact-card-${id}`}
                  className="grid md:grid-cols-[0.85fr_1.15fr] rounded-2xl overflow-hidden bg-[#121216] border border-[#22222B] hover:border-[#C5A059]/35 transition-colors duration-500"
                >
                  <div className={`relative min-h-[220px] md:min-h-full ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <img
                      src={IMAGES[id]}
                      alt={title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className={`absolute inset-0 ${i % 2 === 1 ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-transparent via-transparent to-[#121216] hidden md:block`} />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#121216] md:hidden" />
                  </div>
                  <div className="p-8 sm:p-10">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon size={22} className="text-[#D9A94A] shrink-0" strokeWidth={1.4} />
                      <h4 className="font-heading text-lg sm:text-xl tracking-[0.08em] uppercase text-[#F7F7F8] leading-snug">
                        {title}
                      </h4>
                    </div>
                    <p className="text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light mb-6">{text}</p>
                    {items.length > 0 && (
                      <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                        {items.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-sm text-[#D1D1DB] font-light">
                            <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#C5A059] shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div data-testid="pacts-important-box" className="mt-12 p-7 sm:p-9 rounded-xl bg-[#16161B] border border-[#D9A94A]/25">
            <div className="flex items-center gap-3 mb-5">
              <TriangleAlert size={20} className="text-[#D9A94A]" strokeWidth={1.6} />
              <h4 className="font-heading text-base sm:text-lg tracking-[0.12em] uppercase text-[#F7F7F8]">
                {p.important.title}
              </h4>
            </div>
            <ul className="space-y-3">
              {p.important.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-[#A3A3AF] leading-relaxed font-light">
                  <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#D9A94A] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="mt-20 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl text-[#F7F7F8] leading-snug mb-5">{p.finalTitle}</h3>
          <p className="max-w-xl mx-auto text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light mb-10">
            {p.finalText}
          </p>
          <a
            data-testid="pacts-consult-cta"
            href={whatsappLink(p.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold"
          >
            {p.cta} <ArrowRight size={15} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
